import React from 'react';
import {
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormHelperText,
  Grid,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import * as Icon from 'react-feather';
import { colors } from 'design-system';
import BlueFormLabel from '../BlueFormLabel';
import CatalogItem from '../../../models/helperTypes/CatalogItem';

const useStyles = makeStyles({
  checkWrapper: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: colors.madison,
    color: colors.white,
  },
});

type Props = {
  disabled?: boolean;
  formTitle: string;
  gridSize?: 3 | 6 | 12;
  defaultValue?: string;
  onChange: (val: CatalogItem) => void;
  radioData: CatalogItem[];
  radioGroupName: string;
  boldLabel?: boolean;
  disabledValues?: string[];
  errorMessage?: string;
};

const RadioButtonsGroup = ({
  formTitle,
  radioData,
  radioGroupName,
  onChange,
  gridSize = 3,
  defaultValue = '',
  disabled = false,
  boldLabel = false,
  disabledValues = [],
  errorMessage = '',
}: Props): JSX.Element => {
  const classes = useStyles();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const valueOfRadioButton = (event.target as HTMLInputElement).value;
    const result = radioData
      .filter((item: CatalogItem): boolean => item.Value === valueOfRadioButton)
      .pop();
    if (typeof result !== 'undefined') {
      onChange(result);
    }
  };

  return (
    <FormControl
      component="fieldset"
      fullWidth
      disabled={disabled}
      error={errorMessage !== ''}
    >
      <BlueFormLabel data-testid="blueformlabel" bold={boldLabel}>
        {formTitle}
      </BlueFormLabel>
      <RadioGroup
        name={radioGroupName}
        value={defaultValue}
        onChange={handleChange}
      >
        <Grid container justifyContent="center">
          {radioData.map(
            (radioInfo: CatalogItem): React.ReactNode => (
              <Grid item xs={gridSize} key={radioInfo.Value}>
                <FormControlLabel
                  value={radioInfo.Value}
                  control={
                    <Radio
                      disableRipple
                      checkedIcon={
                        <Grid
                          container
                          alignItems="center"
                          justifyContent="center"
                          className={classes.checkWrapper}
                        >
                          <Icon.Check size={16} />
                        </Grid>
                      }
                    />
                  }
                  label={
                    <Typography variant="caption" color="primary">
                      {radioInfo.Text}
                    </Typography>
                  }
                  disabled={disabledValues.includes(radioInfo.Value)}
                />
              </Grid>
            ),
          )}
        </Grid>
      </RadioGroup>
      <FormHelperText data-testid={`form-helper-text-${radioGroupName}`}>
        {errorMessage}
      </FormHelperText>
    </FormControl>
  );
};

export default RadioButtonsGroup;
